import { Flex, Stack, Text, useDisclosure } from '@chakra-ui/react'
import SubmitDialog from 'BrokerTsLib/src/components/SubmitDialog'
import type { InsuranceParamsDto } from 'BrokerTsLib/src/dtos'
import { InitialGeneralParamsValues, InitialPolicyHolderValues, InitialVehicleValues } from 'BrokerTsLib/src/dtos'
import { InsuranceParamsType, Product } from 'BrokerTsLib/src/enums'
import * as global from 'brokertslib/src/helpers/global'
import { useInsuranceParamsFormPersistance } from 'BrokerTsLib/src/hooks'
import { FormProvider, useForm, yupResolver } from 'BrokerTsLib/src/rhf'
import { formatDate } from 'BrokerTsLib/src/Utils/formatPrice'
import { GeneralParamsPublicPoleasValidationSchema, PolicyHolderPublicPoleasValidationSchema, VehiclePublicPoleasValidationSchema } from 'BrokerTsLib/src/validation'
import cloneDeep from 'lodash/cloneDeep'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { ConfirmModal } from '@/components'
import { ContractReplacementModal } from '@/components/ContractReplacement'
import { CustomWaypoint } from '@/components/CustomWaypoint'
import ErrorSummary from '@/components/ErrorSummary'
import { PageSection } from '@/components/PageSection'
import { UnsavedFormModal } from '@/components/UnsavedFormModal'
import { useInsuranceParams } from '@/context/InsuranceParamsProvider'
import { useSidebar } from '@/context/SidebarContext'
import { useSaveStep1, useUpdateLastWizardStep } from '@/hooks/useWizard'
import type { PageSection as Section } from '@/layouts/components/Sidebar'
import { Information } from '../../vehicle/Step1/containers/Information'
import { Insuree } from '../../vehicle/Step1/containers/Insuree'
import { Vehicle } from './Vehicle'

export const schema = yup.object().shape(
  {
    policyHolder: PolicyHolderPublicPoleasValidationSchema,
    vehicle: VehiclePublicPoleasValidationSchema,
    generalParams: GeneralParamsPublicPoleasValidationSchema,
  }
) as any

export type Step1FormValues = Omit<InsuranceParamsDto, 'baseParams'> // base params je az na K2

export type Step1FormProps = {
  data?: Step1FormValues
  type?: InsuranceParamsType | undefined
}

const defaultValues: Step1FormValues = {
  nextPage: 'next',
  generalParams: InitialGeneralParamsValues,
  policyHolder: InitialPolicyHolderValues,
  vehicle: InitialVehicleValues
}

export const Step1 = ({ data = defaultValues, type }: Step1FormProps) => {
  const [isLoading, setIsLoading] = useState(false)
  const { refetchInsuranceParams, insuranceParams, insuranceParamsId: paramsId, contractReplacement, clearContractReplacement, isDisabled } = useInsuranceParams()
  const navigate = useNavigate()

  const validationSummaryRef = useRef<HTMLDivElement>(null)

  let defaultFormData = { ...data, type: type ?? InsuranceParamsType.Basic }

  const { isOpen: isOpenContractReplacementModal, onClose: onCloseContractReplacementModal, onOpen: onOpenContractReplacementModal } = useDisclosure()
  const { isOpen: isOpenShowInstructionModal, onClose: onCloseShowInstructionModal, onOpen: onOpenShowInstructionModal } = useDisclosure()

  useEffect(() => {
    if (contractReplacement) {
      onOpenContractReplacementModal()
      clearContractReplacement()
    }
  }, [contractReplacement])

  const methods = useForm<Step1FormValues>({
    values: defaultFormData,
    resolver: yupResolver(schema),
    mode: 'onBlur'
  })

  const {
    formState: { errors },
    handleSubmit,
    setValue,
    reset,
    setError,
  } = methods

  const { mutateAsync: updateStep } = useUpdateLastWizardStep()
  const { mutateAsync: saveStep1 } = useSaveStep1()

  // v nevalidniho formulare se nascroluje na validationSummary
  const scrollToValidationSummary = () => {
    setTimeout(() => {
      validationSummaryRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 0)
  }

  const insuranceParamsFormPersistance = useInsuranceParamsFormPersistance(Product.VehicleInsurance, 1, defaultValues.generalParams.paramsId?.toString(), methods)

  const submitForm = useCallback(handleSubmit((data) => {
    setIsLoading(true)
    const values = cloneDeep(data)

    // Datum poslu jen jako date bez casu. To by chtelo vyresit asi nejak obecne. Kdyz se to posle i s casem, tak dochazi k casovemu posunu, coz znamena na serveru predchozi den
    values.generalParams.insuranceStartDate = formatDate(values.generalParams.insuranceStartDate)
    values.vehicle.warrantyStartDate = formatDate(values.vehicle.warrantyStartDate)

    values.policyHolder.person.birthday = formatDate(values.policyHolder.person.birthday)
    if (values.policyHolder.owner) values.policyHolder.owner.birthday = formatDate(values.policyHolder.owner.birthday)

    return saveStep1(values as InsuranceParamsDto)
      .then((response) => {
        reset(data)
        insuranceParamsFormPersistance.clear()

        return (async () => {
          if (values.nextPage == 'next') {
            if ((insuranceParams?.generalParams.lastWizardStep ?? 1) <= 1)
              await updateStep({ paramsId: Number(response?.data), step: 2 })

            if (insuranceParams?.generalParams.paramsId)
              await refetchInsuranceParams()

            // aby se stihl resetovat formular a smazal local storage pred navigaci (jinak se zobrazuje okno s neulozenymi daty)
            setTimeout(() => {
              navigate(`/poleas/wizard/2`)
            }, 0)
          }
          else {
            if (insuranceParams?.generalParams.paramsId)
              await refetchInsuranceParams()

            savedFormAlertOnOpen()
          }

          setIsLoading(false)
        })()
      })
      .catch((err) => {
        setIsLoading(false)

        if (!(err.response && err.response.status === 400 && err.response.data))
          return

        const data = err.response.data

        // zobrazeni serverove validace
        Object.keys(data).forEach((x: any) => {
          setError(global.toCamelCase(x), { message: data[x].join(' ') })
        })

        scrollToValidationSummary()
      })
  }, (err) => {
    scrollToValidationSummary()
  }), [handleSubmit])

  const { pageSections, setPageSections } = useSidebar()

  useEffect(() => {
    let i = 1
    const array: Section[] = [
      {
        id: 'pojistnik',
        sectionTitle: 'Pojistník',
        index: i,
        children: <Insuree number={i++} />
      },
      {
        id: 'vozidlo',
        sectionTitle: 'Vozidlo',
        index: i,
        children: <Vehicle number={i++} />
      },
      {
        id: 'informace',
        sectionTitle: 'Obecné informace',
        index: i,
        children: (
          <Information
            number={i++}
            onContinue={() => {
              setValue('nextPage', 'next')
              return submitForm()
            }}
            onSave={() => {
              setValue('nextPage', 'current')
              return submitForm()
            }}
            isLoading={isLoading}
          />
        )
      },
    ]

    setPageSections(array)
  }, [isLoading, submitForm])

  const {
    isOpen: isSavedFormAlertOpen,
    onOpen: savedFormAlertOnOpen,
    onClose: savedFormAlertOnClose,
  } = useDisclosure()


  useEffect(() => {
    if (!isDisabled)
      onOpenShowInstructionModal()
  }, [isDisabled])

  return (
    <FormProvider {...methods}>
      <form onSubmit={submitForm}>
        <Stack gap={8}>

          {pageSections.map(x => (
            <CustomWaypoint key={x.id} sectionId={x.id}>
              <PageSection id={x.id}>
                {x.children}
              </PageSection>
            </CustomWaypoint>
          ))}

          <ErrorSummary errors={errors} ref={validationSummaryRef} />

          {/* nechci zobrazovat dva modaly pres sebe */}
          {!isOpenContractReplacementModal && <UnsavedFormModal {...insuranceParamsFormPersistance} />}

          <SubmitDialog
            isOpen={isSavedFormAlertOpen}
            onClose={savedFormAlertOnClose}
            header='Uloženo'
            body={!paramsId
              ? 'Nabídka byla uložena do rozpracovaných kalkulací'
              : 'Nabídka byla úspěšně uložena'
            }
          />

          <ContractReplacementModal
            isOpen={isOpenContractReplacementModal}
            onClose={onCloseContractReplacementModal}
            modalContentProps={{ sx: { w: '100%', maxW: '800px' } }}
            title='Náhrada smlouvy'
          />

          <ConfirmModal
            title="Kontrola údajů"
            yesButtonContent="Rozumím"
            isOpen={isOpenShowInstructionModal}
            onClose={onCloseShowInstructionModal}
            isCentered
          >
            <Flex gap={8} alignItems='center'>
              <Text fontSize='xl' fontWeight='semibold'>
                Zkontrolujte prosím správnost všech níže uvedených údajů a případné nesrovnalosti upravte
              </Text>
            </Flex>
          </ConfirmModal>
        </Stack>
      </form>
    </FormProvider >
  )
}
